import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import StepFooter from "../../components/step-footer/StepFooter";
import { ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import "./EspacoDisponivel.css";

import {
  getStoredData,
  clearAppState,
  saveToGlobalState,
} from "../../utils/localStorage";
import { UseFinalizarCadastramentoMala } from "../../hooks/UseFinalizarCadastramentoMala/UseFinalizarCadastramentoMala";

const STORAGE_KEY_ESPACO = "select_espaco_mala";
const STORAGE_KEY_INFO = "informacao_a_viagem";
const STORAGE_KEY_VOLTA = "oferecer_espaco_na_volta";

const EspacoDisponivel = () => {
  const navigate = useNavigate();

  const [estaVoltando, setEstaVoltando] = useState(false);

  const [espacoSelecionado, setEspacoSelecionado] = useState("");
  const [fieldEmptyEspacoSelecionado, setEmptyEspacoSelecionado] =
    useState(false);

  const [infoViagem, setInfoViagem] = useState("");
  const [fieldEmptyInfoViagem, setEmptyInfoViagem] = useState(false);

  const [espacoVoltaSelecionado, setEspacoVoltaSelecionado] = useState("");
  const [fieldEmptyEspacoVolta, setEmptyEspacoVolta] = useState(false);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const topRef = useRef(null);
  const voltaRef = useRef(null);

  const { finalizarCadastramento, loading, setLoading } =
    UseFinalizarCadastramentoMala();

  const espacos = [
    "Pequeno, estilo envelope",
    "Caixa de Sapato/Sacola",
    "Mochila/Mala",
    "Mala/Caixa",
  ];

  const MAX_CHARS = 49;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const data = getStoredData();

    if (data?.[STORAGE_KEY_ESPACO]) {
      setEspacoSelecionado(data[STORAGE_KEY_ESPACO]);
    }

    if (data?.[STORAGE_KEY_INFO]) {
      setInfoViagem(data[STORAGE_KEY_INFO]);
    }

    if (data?.[STORAGE_KEY_VOLTA]) {
      setEspacoVoltaSelecionado(data[STORAGE_KEY_VOLTA]);
    }

    if (data?.voltando_trajeto === "sim") {
      setEstaVoltando(true);

      setEspacoVoltaSelecionado("nao");
    }
  }, []);

  const handleBack = () => {
    const stored = getStoredData();
    const type = stored?.qual_meio_transporte;

    const routes = {
      aviao: "/data",
      onibus: "/data",
      trem: "/data",
      carro: stored?.editar_trajeto === "sim" ? "/data" : "/cidades-trajeto",
      moto: stored?.editar_trajeto === "sim" ? "/data" : "/cidades-trajeto",
    };

    const route = routes[type];
    if (route) navigate(route);
  };

  const scrollToVolta = () => {
    voltaRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleNext = async () => {
    if (!infoViagem) {
      setEmptyInfoViagem(true);

      setTimeout(() => {
        setEmptyInfoViagem(false);
      }, 1500);

      topRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      return;
    }

    if (!espacoSelecionado) {
      setEmptyEspacoSelecionado(true);

      setTimeout(() => {
        setEmptyEspacoSelecionado(false);
      }, 1500);

      topRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      return;
    }

    if (!estaVoltando && !espacoVoltaSelecionado) {
      setEmptyEspacoVolta(true);

      setTimeout(() => {
        setEmptyEspacoVolta(false);
      }, 1500);

      scrollToVolta();

      return;
    }

    const dataStorage = getStoredData();

    try {
      setLoading(true);

      /**
       * Caso seja edição de trajeto
       */
      if (dataStorage?.editar_trajeto === "sim") {
        await finalizarCadastramento();

        clearAppState();

        saveToGlobalState({
          tab_login: "VIAJANTE",
        });

        navigate(`/trips-progress-traveler`, {
          state: {
            item: null,
            notification_type: "trip-updated",
            from: "notification",
          },
        });

        return;
      }

      /**
       * Caso já esteja cadastrando uma volta
       */
      const isVoltando = dataStorage?.voltando_trajeto === "sim";

      if (isVoltando) {
        await finalizarCadastramento();

        clearAppState();

        saveToGlobalState({
          tab_login: "VIAJANTE",
        });

        navigate("/dashboard-traveler");

        return;
      }

      /**
       * Finaliza cadastro da viagem atual
       */
      await finalizarCadastramento();

      const stored = getStoredData();
      const type = stored?.qual_meio_transporte;

      const routes = {
        aviao: "/aviao",
        onibus: "/onibus",
        trem: "/trem",
        carro: "/carro",
        moto: "/moto",
      };

      const route = routes[type];

      /**
       * Usuário quer oferecer espaço na volta
       */
      if (espacoVoltaSelecionado === "sim" && route) {
        clearAppState();

        saveToGlobalState({
          qual_meio_transporte: type,
          voltando_trajeto: "sim",
          tab_login: "VIAJANTE",
        });

        navigate(route);

        return;
      }

      /**
       * Usuário não quer volta
       */
      clearAppState();

      saveToGlobalState({
        tab_login: "VIAJANTE",
        voltando_trajeto: "nao",
      });

      navigate("/dashboard-traveler");
    } catch (err) {
      console.error("Erro ao finalizar cadastro:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter" || loading) return;

      const tag = e.target.tagName;
      if (tag === "TEXTAREA") return;

      if (!espacoSelecionado || !infoViagem || !espacoVoltaSelecionado) return;

      e.preventDefault();
      handleNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [loading, espacoSelecionado, infoViagem, espacoVoltaSelecionado]);

  return (
    <>
      <style>{`
        body {
          overflow-y: auto !important;
        }
      `}</style>

      <div className="page-layout">
        <div className={`espaco-flow ${estaVoltando ? "voltando" : ""}`}>
          <section className="espaco-card travel-card" ref={topRef}>
            <div className="espaco-header">
              <h2 className="espaco-title">
                Descreva a informação da sua viagem
              </h2>
            </div>

            <div className="textarea-wrap">
              <textarea
                className={
                  fieldEmptyInfoViagem
                    ? "espaco-textarea blink-shadow"
                    : "espaco-textarea"
                }
                value={infoViagem}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value.length <= MAX_CHARS) {
                    setInfoViagem(value);
                    saveToGlobalState({
                      [STORAGE_KEY_INFO]: value,
                      tab_login: "VIAJANTE",
                    });
                  }
                }}
                placeholder="Descreva a sua viagem..."
                rows={3}
                style={{ resize: "none" }}
              />

              <div className="char-counter">
                {infoViagem ? infoViagem.length : 0}/{MAX_CHARS}
              </div>
            </div>
          </section>

          {!estaVoltando && (
            <section className="espaco-card volta-card" ref={voltaRef}>
              <div className="espaco-header" style={{ marginBottom: "20px" }}>
                <h2 className="espaco-title">
                  Deseja Oferecer espaço na volta?
                </h2>
              </div>

              <div className="opcoes-wrap">
                <button
                  type="button"
                  className={`opcao-card ${
                    espacoVoltaSelecionado === "sim" ? "ativo" : ""
                  } ${fieldEmptyEspacoVolta ? "blink-shadow" : ""} ${estaVoltando ? "bloqueado" : ""}`}
                  onClick={() => {
                    if (estaVoltando) return;

                    setEspacoVoltaSelecionado("sim");
                    setEmptyEspacoVolta(false);

                    saveToGlobalState({
                      [STORAGE_KEY_VOLTA]: "sim",
                      tab_login: "VIAJANTE",
                    });
                  }}
                >
                  <span className="opcao-titulo">Sim</span>
                  <span className="opcao-subtitulo">Quero oferecer espaço</span>
                </button>

                <button
                  type="button"
                  className={`opcao-card ${
                    espacoVoltaSelecionado === "nao" ? "ativo" : ""
                  } ${fieldEmptyEspacoVolta ? "blink-shadow" : ""} ${estaVoltando ? "bloqueado" : ""}`}
                  onClick={() => {
                    if (estaVoltando) return;

                    setEspacoVoltaSelecionado("nao");
                    setEmptyEspacoVolta(false);

                    saveToGlobalState({
                      [STORAGE_KEY_VOLTA]: "nao",
                      tab_login: "VIAJANTE",
                    });
                  }}
                >
                  <span className="opcao-titulo">Não</span>
                  <span className="opcao-subtitulo">Não quero oferecer</span>
                </button>
              </div>
            </section>
          )}

          <section
            className={`espaco-card principal-card ${estaVoltando ? "bag-size" : ""}`}
          >
            <div className="espaco-coluna">
              <div className="espaco-header">
                <h2 className="espaco-title">Selecione o tamanho da mala</h2>
              </div>

              <div className="select-wrap" ref={wrapRef}>
                <button
                  type="button"
                  className={
                    fieldEmptyEspacoSelecionado
                      ? "espaco-select custom blink-shadow"
                      : "espaco-select custom"
                  }
                  onClick={() => setOpen(!open)}
                >
                  <span className={!espacoSelecionado ? "placeholder" : ""}>
                    {espacoSelecionado || "Selecionar espaço"}
                  </span>
                  <ExpandMore
                    style={{
                      position: "relative",
                      left: 20,
                      color: "#11b9a3",
                      fontSize: 20,
                    }}
                  />
                </button>

                {open && (
                  <div className="espaco-panel">
                    <div className="espaco-list">
                      {espacos.map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`espaco-item ${
                            espacoSelecionado === item ? "active" : ""
                          }`}
                          onClick={() => {
                            setEspacoSelecionado(item);
                            saveToGlobalState({
                              [STORAGE_KEY_ESPACO]: item,
                              tab_login: "VIAJANTE",
                            });
                            setOpen(false);
                          }}
                        >
                          <span>{item}</span>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontFamily: "Gotham",
                              color: "#11b9a3",
                              fontSize: 12,
                              background: "rgba(17,185,163,.10)",
                              padding: "4px 10px",
                              borderRadius: "999px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item === "Pequeno, estilo envelope"
                              ? "até 1kg"
                              : item === "Caixa de Sapato/Sacola"
                                ? "1 a 5kg"
                                : item === "Mochila/Mala"
                                  ? "5 a 10kg"
                                  : item === "Mala/Caixa"
                                    ? "10 a 23kg"
                                    : ""}
                          </Typography>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="espaco-card actions-card">
            <div className="espaco-actions">
              <button type="button" className="btn-back" onClick={handleBack}>
                Voltar
              </button>

              <button
                type="button"
                className="btn-next"
                onClick={handleNext}
                disabled={loading}
              >
                {loading ? "Salvando..." : "Continuar"}
              </button>
            </div>
          </section>
        </div>

        <StepFooter />
      </div>
    </>
  );
};

export default EspacoDisponivel;
